/**
 * Minimal in-card photo carousel for location cards (see
 * sections/our-locations.liquid).
 *
 * This was originally built on Horizon's <slideshow-component> (see
 * snippets/slideshow.liquid / assets/slideshow.js), which kept the carousel's
 * "current slide" in a private JS field on the component instance. The
 * "carousel on mobile" layout renders every card twice: once in the grid
 * (hidden on mobile) and once inside a second, separate carousel wrapper
 * (hidden on desktop), so each gallery-enabled card produced two live
 * <slideshow-component> instances at once, only one of which is ever visible
 * at a given viewport width. Nothing outside the theme was wired to either by
 * name, so they never collided over a ref, but calling select() on the
 * instance whose whole subtree is `display: none` still updated its internal
 * index and its slides' aria-hidden state, while scrollLeft silently stayed
 * put, because a browser will not scroll a hidden element. That let a
 * hidden instance's bookkeeping drift out of sync with its own scroll
 * position, invisibly, for no benefit: two full IntersectionObserver +
 * ResizeObserver + drag-handling setups per gallery card is a lot of
 * machinery for seven decorative photos.
 *
 * <location-gallery> below has no such state to drift: the current slide is
 * never stored anywhere, it is always read back from track.scrollLeft on
 * demand. A hidden duplicate simply never receives a scroll event, so it has
 * nothing to desync in the first place.
 */
class LocationGallery extends HTMLElement {
  #controller = new AbortController();
  #debounceTimer = null;
  #settleTimer = null;

  /** @returns {HTMLElement | null} */
  get #track() {
    return this.querySelector('.location-gallery__track');
  }

  /** @returns {HTMLElement[]} */
  get #slides() {
    return Array.from(this.querySelectorAll('.location-gallery__slide'));
  }

  /** @returns {HTMLButtonElement[]} */
  get #dots() {
    return Array.from(this.querySelectorAll('.location-gallery__dot'));
  }

  /** @returns {HTMLButtonElement | null} */
  get #previousButton() {
    return this.querySelector('.location-gallery__arrow--previous');
  }

  /** @returns {HTMLButtonElement | null} */
  get #nextButton() {
    return this.querySelector('.location-gallery__arrow--next');
  }

  connectedCallback() {
    const { signal } = this.#controller;
    const track = this.#track;

    this.addEventListener('click', this.#handleClick, { signal });

    // Chrome reverts a smooth scroll-snap animation back to its start point
    // if anything in the scroll container's subtree mutates while the
    // animation is still running (aria-hidden flips, the current-dot class,
    // the disabled state on the arrows: all of it counts). #sync() must
    // therefore only run once the scroll has fully settled, never on every
    // 'scroll' tick. 'scrollend' is exactly that signal and both Chrome and
    // Safari support it; only fall back to a debounced 'scroll' listener on
    // engines that do not, and only after movement has stopped for a beat.
    if ('onscrollend' in window) {
      track?.addEventListener('scrollend', this.#handleScrollEnd, { signal });
    } else {
      track?.addEventListener('scroll', this.#handleScrollDebounced, { signal, passive: true });
    }

    this.#sync();
  }

  disconnectedCallback() {
    this.#controller.abort();
    if (this.#debounceTimer) clearTimeout(this.#debounceTimer);
    if (this.#settleTimer) clearTimeout(this.#settleTimer);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('button');
    if (!button || !this.contains(button)) return;

    if (button.classList.contains('location-gallery__arrow--next')) {
      this.#goTo(this.#currentIndex + 1);
    } else if (button.classList.contains('location-gallery__arrow--previous')) {
      this.#goTo(this.#currentIndex - 1);
    } else if (button.classList.contains('location-gallery__dot')) {
      const index = Number(button.dataset.index);
      if (!Number.isNaN(index)) this.#goTo(index);
    }
  };

  #handleScrollEnd = () => {
    this.#sync();
  };

  /**
   * Fallback for engines without the 'scrollend' event. Waits for scroll
   * position to stop changing for 120ms before syncing, so it can never fire
   * while a smooth scroll-snap animation is still in progress.
   */
  #handleScrollDebounced = () => {
    if (this.#debounceTimer) clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => {
      this.#debounceTimer = null;
      this.#sync();
    }, 120);
  };

  /**
   * Derives the current slide index straight from the live scroll position.
   * @returns {number}
   */
  get #currentIndex() {
    const track = this.#track;
    const slides = this.#slides;
    if (!track || slides.length === 0) return 0;

    const slideWidth = slides[0].getBoundingClientRect().width || 1;
    return Math.round(track.scrollLeft / slideWidth);
  }

  /** @param {number} index */
  #goTo(index) {
    const track = this.#track;
    const slides = this.#slides;
    if (!track || slides.length === 0) return;

    const clamped = Math.max(0, Math.min(index, slides.length - 1));
    const target = slides[clamped];
    const left = target.offsetLeft - track.offsetLeft;
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion) {
      track.scrollTo({ left, behavior: 'instant' });
      return;
    }

    track.scrollTo({ left, behavior: 'smooth' });

    /*
      A smooth scroll is a request, not a guarantee. Chrome runs no scroll
      animation at all in a hidden or unfocused tab, and a mandatory scroll-snap
      container will abandon an in-flight smooth scroll if anything reflows
      inside it. Both cases leave the track exactly where it was, which reads to
      the visitor as "the arrows do nothing". So we check whether the scroll
      actually started and, if it did not, jump there outright. Losing the
      animation is a far better outcome than losing the navigation.
    */
    if (this.#settleTimer) clearTimeout(this.#settleTimer);
    const from = track.scrollLeft;
    this.#settleTimer = setTimeout(() => {
      this.#settleTimer = null;
      const moved = Math.abs(track.scrollLeft - from) > 1;
      const arrived = Math.abs(track.scrollLeft - left) <= 1;
      if (!moved && !arrived) {
        track.scrollTo({ left, behavior: 'instant' });
        this.#sync();
      }
    }, 250);
  }

  /**
   * Reflects the current slide in the accessible tree and the controls.
   * Only touches attributes, never re-renders markup.
   */
  #sync() {
    const index = this.#currentIndex;
    const slides = this.#slides;
    const dots = this.#dots;
    const previousButton = this.#previousButton;
    const nextButton = this.#nextButton;

    slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
    });

    dots.forEach((dot, i) => {
      const isCurrent = i === index;
      dot.setAttribute('aria-current', String(isCurrent));
      dot.classList.toggle('location-gallery__dot--current', isCurrent);
    });

    if (previousButton) previousButton.disabled = index <= 0;
    if (nextButton) nextButton.disabled = index >= slides.length - 1;
  }
}

if (!customElements.get('location-gallery')) {
  customElements.define('location-gallery', LocationGallery);
}
